import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, ChevronRight, MessageCircle, Phone, PlayCircle, Search } from 'lucide-react';
import {
  SUPPORT_CATEGORIES,
  type SupportCategory,
  type SupportQuery,
  type Tutorial,
} from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  ListSkeleton,
  useToast,
} from '@tailoring-crm/ui';
import { isApiRequestError } from '../lib/api/errors.js';
import { formatDate } from '../lib/format.js';
import {
  useAskSupport,
  useCloseSupportQuery,
  useReplyToSupport,
  useSupportContact,
  useSupportQueries,
  useTutorials,
} from '../lib/support/use-support.js';

/**
 * HELP — the screen this product did not have.
 *
 * It is sold to people who are not software users. When they get stuck at nine in the evening
 * there is nobody in the building who can help them, and until now the app had nothing to say:
 * no guide, no walkthrough, no way to ask. That is not a missing nicety. It is the reason a
 * shop quietly stops opening the app.
 *
 * The screen is one page with three things on it, in the order somebody actually wants them:
 *
 *  1. **Search the tutorials first.** Most questions have a written answer, and reading one
 *     takes ten seconds against a reply that takes a day.
 *  2. **Reach a person second** — the phone number and the hours, stated plainly, because for
 *     a lot of this market a phone call IS the support channel and pretending otherwise is
 *     just making them find the number somewhere else.
 *  3. **Ask us third**, with what they were doing already filled in.
 *
 * There is no tab bar. A shop owner who is stuck does not want to choose a category of being
 * stuck; scrolling past a search box they have already used costs nothing.
 */

export function SupportPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const location = useLocation();

  const [search, setSearch] = useState('');
  const [openTutorial, setOpenTutorial] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);

  const tutorials = useTutorials();
  const contact = useSupportContact();
  const queries = useSupportQueries({ openOnly: false, pageSize: 20 });

  /**
   * Filtered in the browser, not on the server.
   *
   * The whole catalogue is a dozen entries that arrived in one response and never changes
   * while somebody is typing. A round-trip per keystroke would make the search feel worse than
   * the paper manual it replaces.
   */
  const topics = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const all = tutorials.data?.topics ?? [];
    if (needle.length === 0) return all;
    return all
      .map((topic) => ({
        ...topic,
        items: topic.items.filter((item) =>
          [item.title, item.summary, ...item.steps].join(' ').toLowerCase().includes(needle),
        ),
      }))
      .filter((topic) => topic.items.length > 0);
  }, [search, tutorials.data]);

  const matchCount = topics.reduce((sum, topic) => sum + topic.items.length, 0);

  return (
    <>
      <PageHeader
        title={t('support.title')}
        description={t('support.description')}
        action={
          <Button onClick={() => setAsking(true)}>
            <MessageCircle aria-hidden="true" />
            {t('support.ask')}
          </Button>
        }
      />

      <div className="flex flex-col gap-4">
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            className="pl-9"
            value={search}
            placeholder={t('support.searchPlaceholder')}
            aria-label={t('support.searchPlaceholder')}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {tutorials.isPending ? <ListSkeleton /> : null}

        {!tutorials.isPending && matchCount === 0 ? (
          <EmptyState
            icon={BookOpen}
            title={t('support.noMatches')}
            /* The dead end is where the query form belongs: somebody who searched and found
               nothing is exactly the person who should be asking us. */
            description={t('support.noMatchesHint')}
            action={<Button onClick={() => setAsking(true)}>{t('support.ask')}</Button>}
          />
        ) : null}

        {topics.map((topic) => (
          <Card key={topic.topic}>
            <CardHeader>
              <CardTitle>{t(`support.topic.${topic.topic}`, topic.label)}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              {topic.items.map((item) => (
                <TutorialRow
                  key={item.id}
                  tutorial={item}
                  open={openTutorial === item.id}
                  onToggle={() => setOpenTutorial(openTutorial === item.id ? null : item.id)}
                />
              ))}
            </CardContent>
          </Card>
        ))}

        {/* Reaching a person. Only the rows this deployment has actually configured — a blank
            row under a heading reads as a product that has lost our phone number. */}
        {contact.data && (contact.data.phone ?? contact.data.whatsappUrl ?? contact.data.email) ? (
          <Card>
            <CardHeader>
              <CardTitle>{t('support.contact.title')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 text-sm">
              {contact.data.phone ? (
                <p className="flex items-center gap-2">
                  <Phone aria-hidden="true" className="size-4 text-muted-foreground" />
                  <a className="underline" href={`tel:${contact.data.phone}`}>
                    {contact.data.phone}
                  </a>
                </p>
              ) : null}
              {contact.data.whatsappUrl ? (
                <p>
                  <a
                    className="underline"
                    href={contact.data.whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('support.contact.whatsapp')}
                  </a>
                </p>
              ) : null}
              {contact.data.email ? (
                <p>
                  <a className="underline" href={`mailto:${contact.data.email}`}>
                    {contact.data.email}
                  </a>
                </p>
              ) : null}
              {contact.data.hours ? (
                <p className="text-muted-foreground">{contact.data.hours}</p>
              ) : null}
              {contact.data.address ? (
                <p className="text-muted-foreground">{contact.data.address}</p>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        {asking ? (
          <AskForm
            route={location.pathname}
            onDone={() => {
              setAsking(false);
              toast({ title: t('support.asked'), variant: 'success' });
            }}
            onCancel={() => setAsking(false)}
          />
        ) : null}

        {/* What they have already asked, and what we said. Below the tutorials because it is
            empty for almost everybody, almost always. */}
        {queries.data && queries.data.items.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>{t('support.yourQueries')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {queries.data.items.map((query) => (
                <QueryThread key={query.id} query={query} />
              ))}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </>
  );
}

/* ---------------------------------------------------------------------------------------- */

function TutorialRow({
  tutorial,
  open,
  onToggle,
}: {
  tutorial: Tutorial;
  open: boolean;
  onToggle: () => void;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        className="flex w-full items-start gap-2 p-3 text-left"
        aria-expanded={open}
        onClick={onToggle}
      >
        <ChevronRight
          aria-hidden="true"
          className={`mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform ${
            open ? 'rotate-90' : ''
          }`}
        />
        <span className="min-w-0 flex-1">
          <span className="block font-medium">{tutorial.title}</span>
          <span className="block text-sm text-muted-foreground">{tutorial.summary}</span>
        </span>
        {tutorial.essential ? <Badge variant="secondary">{t('support.essential')}</Badge> : null}
      </button>

      {open ? (
        <div className="flex flex-col gap-3 border-t border-border p-3">
          <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-sm">
            {tutorial.steps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
          <div className="flex flex-wrap gap-2">
            {/* Reading it and doing it in two different places is the commonest failure of
                in-app help. Every tutorial names the screen it teaches. */}
            <Button asChild variant="outline" size="sm">
              <Link to={tutorial.route}>{t('support.openScreen')}</Link>
            </Button>
            {tutorial.videoUrl ? (
              <Button asChild variant="outline" size="sm">
                <a href={tutorial.videoUrl} target="_blank" rel="noreferrer">
                  <PlayCircle aria-hidden="true" />
                  {t('support.watch')}
                </a>
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------------------------------- */

function AskForm({
  route,
  onDone,
  onCancel,
}: {
  route: string;
  onDone: () => void;
  onCancel: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const ask = useAskSupport();

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState<SupportCategory>('how_do_i');

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    try {
      // `route` rides along, filled in by the client and never typed: "it does not work" from
      // a screen we cannot name costs two messages to resolve and one of them is ours.
      await ask.mutateAsync({ subject, message, category, route });
      setSubject('');
      setMessage('');
      onDone();
    } catch (caught) {
      toast({
        title: isApiRequestError(caught) ? caught.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('support.ask')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-3" onSubmit={(event) => void submit(event)}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="support-subject">{t('support.subject')}</Label>
            <Input
              id="support-subject"
              value={subject}
              required
              maxLength={160}
              placeholder={t('support.subjectPlaceholder')}
              onChange={(event) => setSubject(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="support-category">{t('support.category')}</Label>
            <select
              id="support-category"
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={category}
              onChange={(event) => setCategory(event.target.value as SupportCategory)}
            >
              {SUPPORT_CATEGORIES.map((one) => (
                <option key={one} value={one}>
                  {t(`support.categoryLabel.${one}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="support-message">{t('support.message')}</Label>
            <textarea
              id="support-message"
              className="min-h-28 rounded-md border border-input bg-background p-3 text-sm"
              value={message}
              required
              maxLength={4000}
              placeholder={t('support.messagePlaceholder')}
              onChange={(event) => setMessage(event.target.value)}
            />
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={ask.isPending}>
              {t('support.send')}
            </Button>
            <Button type="button" variant="ghost" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------------------------------------- */

function QueryThread({ query }: { query: SupportQuery }): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState('');
  const send = useReplyToSupport(query.id);
  const close = useCloseSupportQuery();

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    try {
      await send.mutateAsync(reply);
      setReply('');
    } catch (caught) {
      toast({
        title: isApiRequestError(caught) ? caught.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        className="flex w-full items-center gap-2 p-3 text-left"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="min-w-0 flex-1">
          <span className="block font-medium">{query.subject}</span>
          <span className="block text-sm text-muted-foreground">
            {query.reference} · {formatDate(query.updatedAt)}
          </span>
        </span>
        <Badge variant={query.status === 'closed' ? 'secondary' : 'default'}>
          {t(`support.status.${query.status}`)}
        </Badge>
      </button>

      {open ? (
        <div className="flex flex-col gap-3 border-t border-border p-3">
          {query.messages.map((message, index) => (
            <div
              key={index}
              className={
                message.author === 'support'
                  ? 'rounded-md bg-muted p-2 text-sm'
                  : 'rounded-md border border-border p-2 text-sm'
              }
            >
              <p className="mb-1 text-xs text-muted-foreground">
                {message.author === 'support'
                  ? t('support.fromUs')
                  : (message.authorName ?? t('support.fromShop'))}
                {' · '}
                {formatDate(message.sentAt)}
              </p>
              <p className="whitespace-pre-wrap">{message.body}</p>
            </div>
          ))}

          {query.status === 'closed' ? (
            <p className="text-sm text-muted-foreground">{t('support.closedNotice')}</p>
          ) : (
            <form className="flex flex-col gap-2" onSubmit={(event) => void submit(event)}>
              <Label htmlFor={`reply-${query.id}`} className="sr-only">
                {t('support.reply')}
              </Label>
              <textarea
                id={`reply-${query.id}`}
                className="min-h-20 rounded-md border border-input bg-background p-3 text-sm"
                value={reply}
                required
                maxLength={4000}
                placeholder={t('support.replyPlaceholder')}
                onChange={(event) => setReply(event.target.value)}
              />
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={send.isPending}>
                  {t('support.reply')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={close.isPending}
                  onClick={() => void close.mutateAsync(query.id)}
                >
                  {t('support.close')}
                </Button>
              </div>
            </form>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default SupportPage;
